import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Link,
  NativeSelect,
  RadioGroup,
  Separator,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../shared/stores/RootStore";
import useAutoResizeMap from "../../../hooks/useAutoResizeMap";
import { useEffect, useState } from "react";
import {
  ArrowBackIcon,
  ArrowRightIcon,
  CheckIcon,
  EditIcon,
  SaveIcon,
} from "../../../shared/views/icons";
import { t } from "i18next";
import { toast } from "../../../shared/views/CustomToaster";
import { MergeIntoMenu } from "./MergeIntoMenu";
import { CrossSectionView } from "./cross-section/CrossSectionView";
import {
  FacilityType,
  getOptionTranslation,
  HeavyVehicleFlowType,
} from "@keita/lanepatrol-sdk";
import { MultiSelectionWarning } from "./form/MultiSelectionWarning";
import { NumberField } from "./form/NumberField";
import { FieldWrapper } from "./form/FieldWrapper";
import { CreateOrUpdateHlaLane } from "../../models/HlaLane";
import { HlaLaneForm } from "./form/HlaLaneForm";
import { RadioOptions } from "./form/RadioOptions";
import { NotApplicable } from "./form/NotApplicable";
import {
  getDefaultAverageDailyTraffic,
  getDefaultAverageRoadSpeed,
  hlaWayToUpdate,
  HlaWayType,
} from "../../models/HlaWay";
import { NewLaneButton } from "./NewLaneButton";
import turf from "../../../shared/utils/turf";
import { hlaAttributeOptions } from "../../../map/models/HlaAttribute";
import { MapMode } from "../../../survey/stores/MapStore";
import {
  CreateSimulationWay,
  simulationLaneToCreateOrUpdate,
} from "../../models/SimulationWay";
import { WayDetailDelete } from "./WayDetailDelete";
import { HlaScoreView } from "../HlaScoreView";

const HlaWayDetailBase = () => {
  useAutoResizeMap();

  const {
    mapStore,
    hlaStore,
    simulationStore,
    surveyStore,
    filterStore,
    streetViewStore,
    hlaForecastStore,
  } = useStores();

  const hlaWay = hlaStore.getSelected()!;
  const survey = surveyStore.getSelected();
  const noPermissions = !survey?.permissions?.canCodeHla;
  const canEdit = !noPermissions && !hlaForecastStore.isForecastModeActivated();
  const selectedLane = hlaStore.getSelectedLane();
  const form = hlaStore.form;
  const showForm = canEdit && !hlaWay.verified;

  const [creatingSimulation, setCreatingSimulation] = useState(false);

  useEffect(() => {
    mapStore.setMode(MapMode.HlaCoding);

    return () => {
      mapStore.setMode(MapMode.SurveyPage);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    hlaStore.setForm(hlaWayToUpdate(hlaWay));
    // eslint-disable-next-line
  }, [hlaWay]);

  useEffect(() => {
    return () => {
      if (!hlaStore.changesSaved) {
        saveChanges();
      }
    };
    // eslint-disable-next-line
  }, [hlaWay.id]);

  const saveChanges = async ({ verify = false } = {}) => {
    if (verify) hlaStore.verify();

    try {
      await hlaStore.saveChanges();
      toast("success", "Saved");
    } catch {
      toast("error", "Failed to save");
    }
  };

  const laneRequiresRoadFields = (lane: CreateOrUpdateHlaLane | null) => {
    if (!lane) return false;

    const isRoad =
      lane.facilityType === FacilityType.OnRoadBicycleLane ||
      lane.facilityType === FacilityType.RoadShoulder ||
      lane.facilityType === FacilityType.MixedTrafficRoadLane;

    const isAdjacentToRoad =
      (lane.adjacentFeaturesLeft.roadLane != null &&
        !lane.adjacentFeaturesLeft.hardSegregation) ||
      (lane.adjacentFeaturesRight.roadLane != null &&
        !lane.adjacentFeaturesRight.hardSegregation);

    return isRoad || isAdjacentToRoad;
  };

  const roadFieldsRequired =
    laneRequiresRoadFields(form.frontFacingLane) ||
    laneRequiresRoadFields(form.backFacingLane);

  const unverifiedWays = filterStore.filteredHlaWays.filter(
    (way) => !way.verified,
  );

  const createSimulation = async () => {
    if (!survey || !hlaWay.id) return;

    setCreatingSimulation(true);

    try {
      const way: CreateSimulationWay = {
        surveyId: survey.id,
        hlaWayId: hlaWay.id,
        name: hlaWay.name ?? "Unknown street",
        type: hlaWay.type,
        averageDailyTraffic: hlaWay.averageDailyTraffic,
        averageRoadSpeed: hlaWay.averageRoadSpeed,
        heavyVehicleFlow: hlaWay.heavyVehicleFlow,
        lit: hlaWay.lit,
        verified: false,
        frontFacingLane: simulationLaneToCreateOrUpdate(hlaWay.frontFacingLane),
        backFacingLane: simulationLaneToCreateOrUpdate(hlaWay.backFacingLane),
      };
      await simulationStore.createSimulated(way);
    } finally {
      setCreatingSimulation(false);
    }
  };

  return (
    <Stack py={2} overflow={"auto"} w={"full"}>
      <HStack px={6}>
        <Box flexShrink={0} width="40px">
          <IconButton
            size="sm"
            variant="ghost"
            onClick={() => {
              hlaStore.clearSelected();
            }}
          >
            <Icon as={ArrowBackIcon} />
          </IconButton>
        </Box>

        <Text px={2} fontWeight="semibold">
          {hlaWay.name ?? "Unknown street"}
        </Text>

        {canEdit && (
          <WayDetailDelete onDelete={() => hlaStore.delete(hlaWay.id)} />
        )}

        {showForm && <MergeIntoMenu />}

        <Spacer />

        <HStack fontSize="sm" gap={4}>
          {showForm && (
            <HStack>
              <Button
                colorPalette="primary"
                variant={"ghost"}
                size="sm"
                fontSize="sm"
                onClick={() => saveChanges()}
                loading={hlaStore.savingChanges}
                disabled={hlaStore.changesSaved && form.verified}
              >
                <SaveIcon /> Save
              </Button>

              <Button
                colorPalette="primary"
                size="sm"
                fontSize="sm"
                onClick={() => saveChanges({ verify: true })}
                loading={hlaStore.savingChanges}
                disabled={hlaStore.changesSaved && form.verified}
              >
                <SaveIcon /> Save and verify
              </Button>
            </HStack>
          )}

          {canEdit && hlaWay.verified && (
            <ButtonGroup
              alignSelf="end"
              size="sm"
              fontSize="sm"
              colorPalette="primary"
            >
              <Button
                variant={"ghost"}
                onClick={() => {
                  simulationStore.backToHla();
                  hlaStore.editHla();
                }}
              >
                <EditIcon /> Edit assessment
              </Button>

              {simulationStore.selectedSimulationWay &&
                !simulationStore.selectedSimulationWay.verified && (
                  <Button onClick={() => simulationStore.editSimulation()}>
                    Go to simulation
                  </Button>
                )}

              {simulationStore.selectedSimulationWay &&
                simulationStore.selectedSimulationWay.verified && (
                  <Button onClick={() => simulationStore.openComparison()}>
                    View comparison
                  </Button>
                )}

              {!simulationStore.selectedSimulationWay && (
                <Button onClick={createSimulation} loading={creatingSimulation}>
                  Create simulation
                </Button>
              )}
            </ButtonGroup>
          )}

          {unverifiedWays.length === 0 ? (
            <HStack fontSize="xs" color="gray.500">
              <CheckIcon /> All verified
            </HStack>
          ) : (
            <Button
              colorPalette="primary"
              size="sm"
              fontSize="sm"
              disabled={!form.verified}
              onClick={async () => {
                const lastPosition =
                  hlaWay.coordinates[hlaWay.coordinates.length - 1];

                const waysAndDistanceToLastPoint = unverifiedWays.map(
                  (way) => ({
                    way,
                    distance: turf.distance(lastPosition, way.coordinates[0]),
                  }),
                );

                const sortedWays = waysAndDistanceToLastPoint
                  .toSorted((a, b) => a.distance - b.distance)
                  .map((x) => x.way);

                hlaStore.select(sortedWays[0].id);
                streetViewStore.goToHla(sortedWays[0]);
              }}
            >
              <ArrowRightIcon /> Next
            </Button>
          )}
        </HStack>
      </HStack>

      <MultiSelectionWarning />

      {noPermissions && (
        <Stack px={6}>
          <Alert.Root status={"info"}>
            <Alert.Indicator />
            <Alert.Description>
              You don't have permission to manage high-level assessments in this
              survey
            </Alert.Description>
          </Alert.Root>
        </Stack>
      )}

      <Stack gap={6}>
        {showForm && (
          <Stack px={6}>
            <Text textStyle={"lg"}>Road</Text>

            <SimpleGrid columns={{ xl: 1, "2xl": 2 }} gap={6}>
              <FieldWrapper label={t(`shared:hlaAttribute.type`)}>
                <NativeSelect.Root
                  size="sm"
                  fontSize={"sm"}
                  disabled={!canEdit || hlaWay.verified}
                  invalid={form.type == null}
                >
                  <NativeSelect.Field
                    value={form.type}
                    onChange={(e) => {
                      const value = e.target.value;
                      hlaStore.code((prev) => ({
                        ...prev,
                        type: value as HlaWayType,
                      }));
                    }}
                  >
                    {hlaAttributeOptions.type!.map((option) => (
                      <option value={option.value} key={option.value}>
                        {t(option.translation)}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </FieldWrapper>

              <FieldWrapper label={t(`shared:hlaAttribute.heavyVehicleFlow`)}>
                {roadFieldsRequired ? (
                  <RadioGroup.Root
                    colorPalette={"primary"}
                    disabled={!canEdit || hlaWay.verified}
                    value={form.heavyVehicleFlow}
                    onValueChange={({ value }) => {
                      hlaStore.code((prev) => ({
                        ...prev,
                        heavyVehicleFlow: value as HeavyVehicleFlowType,
                      }));
                    }}
                  >
                    <RadioOptions
                      options={hlaAttributeOptions.heavyVehicleFlow!}
                    />
                  </RadioGroup.Root>
                ) : (
                  <NotApplicable />
                )}
              </FieldWrapper>

              <FieldWrapper
                label={t("shared:hlaAttribute.averageRoadSpeed", {
                  speedUnit: surveyStore.speedUnitStr,
                })}
              >
                <NumberField
                  step={10}
                  disabled={!canEdit || hlaWay.verified}
                  onChange={(value) => {
                    hlaStore.code((prev) => ({
                      ...prev,
                      averageRoadSpeed: value,
                    }));
                  }}
                  value={form.averageRoadSpeed}
                  notApplicable={!roadFieldsRequired}
                />

                {form.averageRoadSpeed == null && (
                  <Link
                    fontSize="xs"
                    onClick={() => {
                      surveyStore.setShowSurveyDefaultsModal(true);
                    }}
                  >
                    {`Value defaults to ${surveyStore.getDefaultAverageRoadSpeed(
                      form.type,
                    )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                  </Link>
                )}
              </FieldWrapper>

              <FieldWrapper
                label={t(`shared:hlaAttribute.averageDailyTraffic`)}
              >
                <NumberField
                  step={1000}
                  disabled={!canEdit || hlaWay.verified}
                  onChange={(value) => {
                    hlaStore.code((prev) => ({
                      ...prev,
                      averageDailyTraffic: value,
                    }));
                  }}
                  value={form.averageDailyTraffic}
                  notApplicable={!roadFieldsRequired}
                />

                {form.averageDailyTraffic == null && (
                  <Link
                    fontSize="xs"
                    onClick={() => {
                      surveyStore.setShowSurveyDefaultsModal(true);
                    }}
                  >
                    {`Value defaults to ${surveyStore.getDefaultAverageDailyTraffic(
                      form.type,
                    )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                  </Link>
                )}
              </FieldWrapper>

              <FieldWrapper label={t(`shared:hlaAttribute.lit`)}>
                <Checkbox.Root
                  disabled={!canEdit || hlaWay.verified}
                  colorPalette="primary"
                  checked={form.lit}
                  onCheckedChange={({ checked }) => {
                    hlaStore.code((prev) => ({
                      ...prev,
                      lit: checked as boolean,
                    }));
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                </Checkbox.Root>
              </FieldWrapper>
            </SimpleGrid>
          </Stack>
        )}

        {(!canEdit || hlaWay.verified) && (
          <VStack alignItems={"start"} p={6} fontSize={"sm"}>
            <HStack>
              <Text fontWeight={"semibold"}>
                {t(`shared:hlaAttribute.type`)}:
              </Text>
              <Text>{form.type}</Text>
            </HStack>
            <HStack>
              <Text fontWeight={"semibold"}>
                {t("shared:hlaAttribute.averageRoadSpeed", {
                  speedUnit: surveyStore.speedUnitStr,
                })}
                :
              </Text>
              <Text>
                {form.averageRoadSpeed ??
                  getDefaultAverageRoadSpeed(
                    form.type,
                    surveyStore.distanceUnit,
                  )}
              </Text>
            </HStack>
            <HStack>
              <Text fontWeight={"semibold"}>
                {t(`shared:hlaAttribute.averageDailyTraffic`)}:
              </Text>
              <Text>
                {form.averageDailyTraffic ??
                  getDefaultAverageDailyTraffic(form.type)}
              </Text>
            </HStack>
            <HStack>
              <Text fontWeight={"semibold"}>
                {t(`shared:hlaAttribute.heavyVehicleFlow`)}:
              </Text>
              <Text>{form.heavyVehicleFlow}</Text>
            </HStack>
            <HStack>
              <Text fontWeight={"semibold"}>
                {t(`shared:hlaAttribute.lit`)}:
              </Text>
              <Text>{form.lit ? "Yes" : "No"}</Text>
            </HStack>
          </VStack>
        )}

        <Stack>
          <Separator />
          <Text textStyle={"lg"} px={6}>
            Bike lanes
          </Text>

          {!showForm && (
            <Stack py={2} px={6} gap={6} overflow={"auto"} w={"full"}>
              <FieldWrapper label={t(`shared:hlaAttribute.facilityType`)}>
                <Text fontSize="sm">
                  {t(getOptionTranslation(selectedLane?.facilityType!))}
                </Text>
              </FieldWrapper>
            </Stack>
          )}

          <HStack
            justifyContent={"center"}
            flexDir={surveyStore.rightHandTraffic ? "row" : "row-reverse"}
          >
            {form.backFacingLane ? (
              <Stack>
                <CrossSectionView
                  lit={form.lit}
                  lane={form.backFacingLane}
                  canEdit={showForm}
                  isSelected={
                    form.backFacingLane.frontFacing ===
                    selectedLane?.frontFacing
                  }
                  score={hlaWay.backFacingLane?.score}
                  onSelect={() => hlaStore.setDefaultToFrontFacing(false)}
                  onDelete={() => {
                    hlaStore.code((prev) => {
                      prev.backFacingLane = null;
                      return { ...prev };
                    });
                    hlaStore.setDefaultToFrontFacing(true);
                  }}
                />

                {hlaWay.verified && hlaWay.backFacingLane?.score != null && (
                  <HlaScoreView score={hlaWay.backFacingLane.score} />
                )}
              </Stack>
            ) : !hlaWay.verified ? (
              <NewLaneButton frontFacing={false} canEdit={canEdit} />
            ) : null}

            {form.frontFacingLane ? (
              <Stack>
                <CrossSectionView
                  lit={form.lit}
                  lane={form.frontFacingLane}
                  canEdit={showForm}
                  isSelected={
                    form.frontFacingLane.frontFacing ===
                    selectedLane?.frontFacing
                  }
                  score={hlaWay.frontFacingLane?.score}
                  onSelect={() => hlaStore.setDefaultToFrontFacing(true)}
                  onDelete={() => {
                    hlaStore.code((prev) => {
                      prev.frontFacingLane = null;
                      return { ...prev };
                    });
                    hlaStore.setDefaultToFrontFacing(false);
                  }}
                />

                {hlaWay.verified && hlaWay.frontFacingLane?.score != null && (
                  <HlaScoreView score={hlaWay.frontFacingLane.score} />
                )}
              </Stack>
            ) : !hlaWay.verified ? (
              <NewLaneButton frontFacing={true} canEdit={canEdit} />
            ) : null}
          </HStack>
        </Stack>

        {!!selectedLane && !hlaWay.verified && canEdit && (
          <HlaLaneForm lane={selectedLane} code={hlaStore.code} way={hlaWay} />
        )}
      </Stack>
    </Stack>
  );
};

export const HlaWayDetail = observer(HlaWayDetailBase);
